/**
 * @class ShopTheLook
 * @classdesc The Shop the Look section. Displays an image
 *            with interactable points directing customers to featured products.
 * @extends Core.SectionComponent
 */
export default class ShopTheLook extends Core.SectionComponent {
  constructor(el, context) {
    super(el, context);

    this._imageWrapperEl = this.el.querySelector('[data-image-wrapper]');
    this._viewInListEl = this.el.querySelector('[data-hotspot-viewinlist]');
    this._hotspotEls = this.el.querySelectorAll('[data-hotspot]');
    this._hotspots = [];
    this._addTheLookFragment = this.el.querySelector('[data-add-the-look-template]').content.cloneNode(true);
    this._addTheLook = null;
    this._currentAnimation = Promise.resolve();

    this._validHotspots = 0;
    this._hotspotEls.forEach(hotspotEl => {
      const toggleEl = hotspotEl.querySelector('[data-hotspot-toggle]');
      const modalEl = hotspotEl.querySelector('[data-hotspot-modal]');
      const animation = Core.Animations.transition({ el: modalEl, state: 'closed' });
      const { hotspot: id, hotspotUrl: productUrl, hotspotPage: page } = hotspotEl.dataset;
      const valid = !!productUrl;

      this._hotspots.push({
        id,
        el: hotspotEl,
        toggleEl,
        modalEl,
        productUrl,
        animation,
        page,
        selectedOptions: null,
        quickshop: null,
        zIndex: 1,
        valid,
      });

      if (valid) {
        this._validHotspots++;
      }
    });
    this._hotspotPages = this._validHotspots + 1;
  }

  onMount() {
    this._drawerModal = Core.Components.getComponent('drawer-modal');

    if (this._drawerModal) {
      this._drawerFlyout = this._drawerModal.createFlyout();
    }

    this.events.register(this._viewInListEl, 'click', e => {
      e.preventDefault();
      e.stopPropagation();

      this._openDrawerWithAddTheLook(this._viewInListEl);
    });

    this.events.register(document.body, 'click', e => {
      if (
        this._hotspots.some(({ el }) => el === e.target || el.contains(e.target))
      ) {
        return;
      }

      this._hotspots.forEach(hotspot => this._closeHotspot(hotspot));
    });

    this._hotspots.forEach(hotspot => {
      const {
        toggleEl,
        modalEl,
        productUrl,
      } = hotspot;

      this.events.register(toggleEl, 'click', async e => {
        e.preventDefault();

        const { open, cause } = await this._getHotspotState(hotspot);

        if (!open || (open && cause !== 'click')) {
          this._openHotspot(hotspot, 'click');
        } else {
          this._closeHotspot(hotspot);
        }
      });

      this.events.register(toggleEl, 'mouseover', async () => {
        const { open, cause } = await this._getHotspotState(hotspot);

        // Preload quickshop
        this._retrieveQuickshop(hotspot);

        if (!open || (open && cause !== 'click')) {
          this._openHotspot(hotspot, 'mouseover');
        }
      });

      this.events.register(modalEl, 'mouseover', async () => {
        const { open, cause } = await this._getHotspotState(hotspot);

        if (!open || (open && cause !== 'click')) {
          this._openHotspot(hotspot, 'mouseover');
        }
      });

      this.events.register(toggleEl, 'mouseleave', async e => {
        const { open, cause } = await this._getHotspotState(hotspot);

        // Do not do anything if we mouseover the product
        if (e.relatedTarget === modalEl || modalEl.contains(e.relatedTarget)) return;

        if (open && cause !== 'click') {
          this._closeHotspot(hotspot);
        }
      });

      this.events.register(modalEl, 'mouseleave', async e => {
        const { open, cause } = await this._getHotspotState(hotspot);

        // Do not do anything if we mouseover the toggle
        if (e.relatedTarget === toggleEl || toggleEl.contains(e.relatedTarget)) return;

        if (open && cause !== 'click') {
          this._closeHotspot(hotspot);
        }
      });

      if (productUrl) {
        this.events.register(modalEl, 'product-details:view-full-details', e => {
          e.stopPropagation();
          e.preventDefault();

          this._openDrawerWithHotspot(e.target, hotspot);
        });
      }
    });
  }

  onDismount() {
    super.onDismount();

    if (this._drawerFlyout) {
      this._drawerFlyout.then(drawerFlyout => drawerFlyout.destroy());
    }
  }

  onBlockSelect(event) {
    const hotspot = this._getHotspotById(event.detail.blockId);

    this._openHotspot(hotspot, 'click');
  }

  onBlockDeselect(event) {
    const hotspot = this._getHotspotById(event.detail.blockId);

    this._closeHotspot(hotspot);
  }

  _getHotspotById(id) {
    return this._hotspots.find(hotspot => hotspot.id === id);
  }

  _getHotspotByEl(el) {
    return this._hotspots.find(hotspot => hotspot.el === el);
  }

  _getHotspotByToggleEl(toggleEl) {
    return this._hotspots.find(hotspot => hotspot.toggleEl === toggleEl);
  }

  _getHotspotByModalEl(modalEl) {
    return this._hotspots.find(hotspot => hotspot.modalEl === modalEl);
  }

  _getHotspotState(hotspot) {
    return this
      ._currentAnimation
      .then(() => {
        const { el } = hotspot;

        return {
          open: el.open,
          cause: el.open ? el.getAttribute('data-hotspot-visible-by') : null,
        };
      });
  }

  _openHotspot(hotspot, cause = null) {
    return this
      ._currentAnimation
      .then(() => this._getHotspotState(hotspot))
      .then(({ open }) => {
        const {
          el,
          toggleEl,
          modalEl,
          animation,
        } = hotspot;

        if (cause) {
          el.setAttribute('data-hotspot-visible-by', cause);
        } else {
          el.removeAttribute('data-hotspot-visible-by');
        }

        hotspot.zIndex = this._hotspots.reduce((acc, h) => {
          if (hotspot === h) return acc;

          if (h.zIndex >= acc) return h.zIndex + 1;

          return acc;
        }, hotspot.zIndex);
        el.style.zIndex = hotspot.zIndex;

        if (open) return Promise.resolve(); // Already open

        el.open = true;
        this._findOpenSpaceForModal(toggleEl, modalEl);

        return animation.animateTo('opened');
      });
  }

  _closeHotspot(hotspot) {
    return this
      ._currentAnimation
      .then(() => this._getHotspotState(hotspot))
      .then(({ open }) => {
        const { animation } = hotspot;

        if (!open) return Promise.resolve(); // Already closed

        return animation
          .animateTo('closed')
          .then(() => open);
      })
      .then(open => {
        const { el } = hotspot;

        el.removeAttribute('data-hotspot-visible-by');

        if (!open) return;

        el.open = false;
        el.style.zIndex = '';
        hotspot.zIndex = 1;
      });
  }

  _findOpenSpaceForModal(toggleEl, modalEl) {
    const directionClasses = [
      'shop-the-look__hotspot-modal--right',
      'shop-the-look__hotspot-modal--left',
      'shop-the-look__hotspot-modal--bottom',
      'shop-the-look__hotspot-modal--top',
    ];

    const fudgeFactor = 1.075; // Add for spacing between product and toggle
    const {
      top: topImageEdge,
      right: rightImageEdge,
      bottom: bottomImageEdge,
      left: leftImageEdge,
    } = this._imageWrapperEl.getBoundingClientRect();
    const {
      top: topToggleEdge,
      right: rightToggleEdge,
      bottom: bottomToggleEdge,
      left: leftToggleEdge,
    } = toggleEl.getBoundingClientRect();
    const {
      width: productWidth,
      height: productHeight,
    } = modalEl.getBoundingClientRect();

    const topEdge = topToggleEdge - productHeight * fudgeFactor;
    const rightEdge = rightToggleEdge + productWidth * fudgeFactor;
    const bottomEdge = bottomToggleEdge + productHeight * fudgeFactor;
    const leftEdge = leftToggleEdge - productWidth * fudgeFactor;

    if (rightEdge <= rightImageEdge) {
      // Within bounds of right side
      modalEl.classList.remove(...directionClasses);
      modalEl.classList.add(directionClasses[0]);
    } else if (leftEdge >= leftImageEdge) {
      // Within bounds of left side
      modalEl.classList.remove(...directionClasses);
      modalEl.classList.add(directionClasses[1]);
    } else if (bottomEdge <= bottomImageEdge) {
      // Within bounds of bottom side
      modalEl.classList.remove(...directionClasses);
      modalEl.classList.add(directionClasses[2]);
    } else if (topEdge >= topImageEdge) {
      // Within bounds of top side
      modalEl.classList.remove(...directionClasses);
      modalEl.classList.add(directionClasses[3]);
    }
  }

  _retrieveQuickshop(hotspot) {
    const { productUrl } = hotspot;

    if (!productUrl) return null;

    if (!hotspot.quickshop) {
      hotspot.quickshop = fetch(productUrl.includes('?') ? `${productUrl}&view=quickshop` : `${productUrl}?view=quickshop`)
        .then(response => response.text()) // Return text string of the HTML
        .then(text => {
          // Convert the HTML string into a document object
          const productContent = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('[data-component="product-quickshop"]');
          const quickshopSection = productContent.parentElement;

          return quickshopSection;
        });
    }

    return hotspot.quickshop;
  }

  /**
   * Opens a quickshop using a drawer modal
   *
   * @param {HTMLNode} trigger The trigger that was clicked to open the quickshop
   * @param {Object} data An object containing data relevant to the quickshop
   * @param {String} data.productLink The link to fetch the product quickshop content
   */
  async _openDrawerWithHotspot(trigger, hotspot) {
    if (!this._drawerFlyout) return;

    const drawerFlyout = await this._drawerFlyout;

    this._drawerModal.openFlyout(drawerFlyout, trigger);
    this._refreshDrawerWithHotspot(hotspot);
  }

  async _openDrawerWithAddTheLook(trigger) {
    if (!this._drawerFlyout) return;

    const drawerFlyout = await this._drawerFlyout;

    this._drawerModal.openFlyout(drawerFlyout, trigger);
    this._refreshDrawerWithAddTheLook();
  }

  async _refreshDrawerWithHotspot(hotspot) {
    if (!this._drawerFlyout) return;

    const drawerFlyout = await this._drawerFlyout;
    const addTheLookHeadingEl = this._addTheLookFragment.querySelector('[data-add-the-look-quickshop-heading]').cloneNode(true);
    const addTheLookFooterEl = this._addTheLookFragment.querySelector('[data-add-the-look-footer]').cloneNode(true);
    const addTheLookAtc = addTheLookFooterEl.querySelector('[data-add-the-look-atc]');

    addTheLookAtc.remove();

    this
      ._retrieveQuickshop(hotspot)
      .then(quickshopSectionEl => {
        drawerFlyout.populateHeading(addTheLookHeadingEl, true);
        drawerFlyout.clearNotification();
        drawerFlyout.populateContent(quickshopSectionEl, true);
        drawerFlyout.populateFooter(addTheLookFooterEl, true);

        this.events.register(quickshopSectionEl, 'product:options-change', event => {
          const { options } = event.detail;

          hotspot.selectedOptions = options;
        });

        Core.Components
          .awaitComponents(quickshopSectionEl)
          .then(components => {
            const productQuickshop = components.filter(component => component.name === 'product-quickshop');
            const { optionsSelector } = productQuickshop;

            if (optionsSelector) {
              if (hotspot.selectedOptions) {
                optionsSelector
                  .setSelectedOptions(hotspot.selectedOptions);
              } else {
                const options = optionsSelector.getSelectedOptions();

                hotspot.selectedOptions = options;
              }
            }

            this._initAddTheLookNavigation(addTheLookFooterEl, parseInt(hotspot.page, 10));
          });
      });
  }

  async _refreshDrawerWithAddTheLook() {
    if (!this._drawerFlyout) return;

    const drawerFlyout = await this._drawerFlyout;
    const addTheLookEl = this._addTheLook ? this._addTheLook : this._addTheLookFragment.querySelector('[data-add-the-look]');
    const addTheLookHeadingEl = this._addTheLookFragment.querySelector('[data-add-the-look-heading]').cloneNode(true);
    const addTheLookFooterEl = this._addTheLookFragment.querySelector('[data-add-the-look-footer]').cloneNode(true);

    const awaitingAddTheLookEl = Core.Components.awaitComponents(addTheLookEl);
    const awaitingAddTheLookFooter = Core.Components.awaitComponents(addTheLookFooterEl);

    drawerFlyout.populateHeading(addTheLookHeadingEl, true);
    drawerFlyout.populateContent(addTheLookEl, true);
    drawerFlyout.populateFooter(addTheLookFooterEl, true);

    Promise.all([
      awaitingAddTheLookEl,
      awaitingAddTheLookFooter,
    ]).then(([addTheLookComponents, addTheLookFooterComponents]) => {
      const addTheLookItems = addTheLookComponents.filter(component => component.name === 'add-the-look-item');
      const addTheLookAtc = addTheLookFooterComponents.find(component => component.name === 'button-action');

      this._initAddTheLookItems(addTheLookItems, addTheLookAtc);
      this._initAddTheLookAtc(addTheLookAtc, addTheLookItems);
      this._initAddTheLookNavigation(
        addTheLookFooterEl,
        this._hotspotPages,
      );

      this._updateAddTheLookNotification(addTheLookItems);
      this._updateAddTheLookAtc(addTheLookAtc, addTheLookItems);

      this._addTheLook = addTheLookEl;
    });
  }

  _getAddTheLookItemState(addTheLookItems) {
    let selectedItems = 0;
    let itemsRequiringOptionSelection = 0;

    addTheLookItems.forEach(addTheLookItem => {
      const { optionsSelector, formData } = addTheLookItem;

      if (formData.get('add_item')) {
        selectedItems++;

        if (!formData.get('id') || (optionsSelector && optionsSelector.getSelectedOptions().some(option => option === ''))) {
          itemsRequiringOptionSelection++;
        }
      }
    });

    return {
      selectedItems,
      itemsRequiringOptionSelection,
    };
  }

  _initAddTheLookNavigation(navigationEls, currentPage) {
    const navigationPrev = navigationEls.querySelector('[data-add-the-look-navigation-previous]');
    const navigationPagesEl = navigationEls.querySelector('[data-add-the-look-navigation-pages]');
    const navigationNext = navigationEls.querySelector('[data-add-the-look-navigation-next]');

    navigationPrev.disabled = currentPage === 1;
    navigationNext.disabled = currentPage === this._hotspotPages;

    navigationPagesEl.innerHTML = navigationPagesEl
      .innerHTML
      .replace('||current_page||', currentPage)
      .replace('||total_pages||', this._hotspotPages);

    this.events.register(navigationPrev, 'mouseover', () => {
      const previousPage = currentPage - 1;
      const previousHotspot = this._hotspots
        .find(({ page }) => page === previousPage.toString());

      if (previousHotspot) {
        this._retrieveQuickshop(previousHotspot);
      }
    });

    this.events.register(navigationNext, 'mouseover', () => {
      const nextPage = currentPage + 1;
      const nextHotspot = this._hotspots
        .find(({ page }) => page === nextPage.toString());

      if (nextHotspot) {
        this._retrieveQuickshop(nextHotspot);
      }
    });

    this.events.register(navigationPrev, 'click', () => {
      const previousPage = currentPage - 1;
      const previousHotspot = this._hotspots
        .find(({ page }) => page === previousPage.toString());

      if (previousHotspot) {
        this._refreshDrawerWithHotspot(previousHotspot);
      }
    });

    this.events.register(navigationNext, 'click', () => {
      const nextPage = currentPage + 1;
      const nextHotspot = this._hotspots
        .find(({ page }) => page === nextPage.toString());

      if (nextPage === this._hotspotPages) {
        this._refreshDrawerWithAddTheLook();
      } else if (nextHotspot) {
        this._refreshDrawerWithHotspot(nextHotspot);
      }
    });
  }

  _initAddTheLookItems(addTheLookItems, addTheLookAtc) {
    addTheLookItems.forEach(addTheLookItem => {
      const hotspotId = addTheLookItem.el.parentElement.dataset.addTheLookItemHotspot;
      const hotspot = this._getHotspotById(hotspotId);
      const { optionsSelector } = addTheLookItem;

      if (optionsSelector) {
        if (hotspot.selectedOptions) {
          optionsSelector
            .setSelectedOptions(hotspot.selectedOptions);
        } else {
          const options = optionsSelector.getSelectedOptions();

          hotspot.selectedOptions = options;
        }
      }

      this.events.register(addTheLookItem.el, 'change', () => {
        this._updateAddTheLookNotification(addTheLookItems);
        this._updateAddTheLookAtc(addTheLookAtc, addTheLookItems);
      });

      this.events.register(addTheLookItem.el, 'product:options-change', event => {
        const { options } = event.detail;

        hotspot.selectedOptions = options;

        this._updateAddTheLookNotification(addTheLookItems);
        this._updateAddTheLookAtc(addTheLookAtc, addTheLookItems);
      });
    });
  }

  async _updateAddTheLookNotification(addTheLookItems) {
    if (!this._drawerFlyout) return;

    const drawerFlyout = await this._drawerFlyout;
    const {
      selectedItems,
      itemsRequiringOptionSelection,
    } = this._getAddTheLookItemState(addTheLookItems);

    const addTheLookNotification = this._addTheLookFragment.querySelector('[data-add-the-look-notification]').cloneNode(true);
    const notificationError = addTheLookNotification.querySelector('[data-add-the-look-notification-error]');
    const notificationSelected = addTheLookNotification.querySelector('[data-add-the-look-notification-selected]');
    const notificationRequiresOptionSelections = addTheLookNotification
      .querySelector('[data-add-the-look-notification-requires-option-selection]');

    notificationError.remove();

    notificationSelected.innerHTML = notificationSelected
      .innerHTML
      .replace('||amount||', selectedItems)
      .replace('||total_items||', this._validHotspots);

    if (itemsRequiringOptionSelection > 0) {
      notificationRequiresOptionSelections.innerHTML = notificationRequiresOptionSelections
        .innerHTML
        .replace('||amount||', itemsRequiringOptionSelection)
        .replace('||total_selected||', selectedItems);
    } else {
      notificationRequiresOptionSelections.remove();
    }

    drawerFlyout.populateNotification(addTheLookNotification, true, true);
  }

  _updateAddTheLookAtc(addTheLookAtc, addTheLookItems) {
    const {
      selectedItems,
      itemsRequiringOptionSelection,
    } = this._getAddTheLookItemState(addTheLookItems);

    if (selectedItems === 0 || itemsRequiringOptionSelection > 0) {
      addTheLookAtc.disabled = true;
      addTheLookAtc.text = this.data.locales.select_variant;
    } else {
      addTheLookAtc.disabled = false;
      addTheLookAtc.text = this.data.locales.add_to_cart;
    }
  }

  async _initAddTheLookAtc(addTheLookAtc, addTheLookItems) {
    if (!this._drawerFlyout) return;

    const drawerFlyout = await this._drawerFlyout;
    const { el: addTheLookAtcEl } = addTheLookAtc;

    this.events.register(addTheLookAtcEl, 'click', () => {
      addTheLookAtc.loading = true;

      const formDatas = addTheLookItems.map(addTheLookItem => addTheLookItem.formData);
      const combinedFormData = {
        items: [],
      };

      formDatas.forEach(formData => {
        if (formData.get('add_item')) {
          combinedFormData.items.push(Object.fromEntries(formData));
        }
      });

      fetch(
        `${window.PXUTheme.routes.cart_add_url}.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(combinedFormData),
        },
      )
        .then(response => (
          Promise.all([
            response,
            fetch(
              `${window.PXUTheme.routes.cart_url}.js`,
              {
                method: 'GET',
              },
            ),
          ])
        ))
        .then(([response1, response2]) => Promise.all([response1.json(), response2.json()]))
        .then(([response, cart]) => {
          if (response.status) {
            const addToCartErrorEvent = new CustomEvent('product:add-to-cart:error', { detail: { cart, error: response }, bubbles: true });
            const addTheLookNotification = this._addTheLookFragment.querySelector('[data-add-the-look-notification]').cloneNode(true);
            const notificationError = addTheLookNotification.querySelector('[data-add-the-look-notification-error]');
            const notificationSelected = addTheLookNotification.querySelector('[data-add-the-look-notification-selected]');
            const notificationRequiresOptionSelections = addTheLookNotification
              .querySelector('[data-add-the-look-notification-requires-option-selection]');

            this.el.dispatchEvent(addToCartErrorEvent);

            notificationSelected.remove();
            notificationRequiresOptionSelections.remove();
            notificationError.innerText = response.description;
            drawerFlyout.populateNotification(addTheLookNotification, true, true);

            return;
          }

          if (window.location.pathname === '/cart') {
            window.location.reload();
            return;
          }

          const addToCartEvent = new CustomEvent('product:add-to-cart:success', { detail: { cart, line_item: response }, bubbles: true });
          this.el.dispatchEvent(addToCartEvent);

          const cartDrawer = Core.Components.getComponent('cart-drawer');

          if (cartDrawer && cartDrawer.data.enabled) {
            cartDrawer.open(addTheLookAtc.el, true, response.items);
          } else {
            window.location = window.PXUTheme.routes.cart_url;
          }
        })
        .catch(err => console.error('Error:', err))
        .finally(() => { addTheLookAtc.loading = false; });
    });
  }
}
